import { createServerFn } from "@tanstack/react-start";

export type BrewerySiteDetail = {
  id: string;
  official_name: string;
  site_type: string | null;
  site_status: string | null;
  address_line_1: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  opened_year: number | null;
  closed_year: number | null;
  taproom: boolean | null;
  restaurant: boolean | null;
  visitor_center: boolean | null;
  tours_available: boolean | null;
  company_shop: boolean | null;
  city: { id: string; name: string } | null;
  admin_area: { id: string; name: string } | null;
  country: { id: string; name: string; iso2: string | null } | null;
  brewery: { id: string; name: string; website_url: string | null } | null;
  operator: { id: string; name: string } | null;
};

export const getBrewerySite = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<BrewerySiteDetail | null> => {
    console.log("[getBrewerySite] start", { id: data.id });
    console.log("[getBrewerySite] env", {
      hasUrl: !!process.env.SUPABASE_URL,
      hasKey: !!process.env.SUPABASE_PUBLISHABLE_KEY,
    });
    let createClient: typeof import("@supabase/supabase-js").createClient;
    try {
      ({ createClient } = await import("@supabase/supabase-js"));
    } catch (e) {
      console.error("[getBrewerySite] failed to import supabase-js", e);
      throw e;
    }
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    const { data: row, error } = await supabase
      .from("cid_brewery_sites")
      .select(
        `id, official_name, site_type, site_status, address_line_1, postal_code,
         latitude, longitude, opened_year, closed_year,
         taproom, restaurant, visitor_center, tours_available, company_shop,
         cid_cities(id, official_name),
         cid_admin_areas(id, official_name),
         cid_countries(id, official_name_en, iso2),
         cid_breweries(id, official_name, website_url),
         operator:cid_organizations!cid_brewery_sites_operator_organization_id_fkey(id, official_name)`,
      )
      .eq("id", data.id)
      .eq("publish_status", "PUBLISHED")
      .maybeSingle();

    console.log("[getBrewerySite] query done", {
      hasRow: !!row,
      errorCode: error?.code,
      errorMessage: error?.message,
      errorDetails: error?.details,
      errorHint: error?.hint,
    });

    if (error) {
      console.error("[getBrewerySite] supabase error", error);
      throw error;
    }
    if (!row) {
      console.log("[getBrewerySite] no row found for id", data.id);
      return null;
    }


    try {
      const pickOne = <T,>(v: T | T[] | null | undefined): T | null =>
        Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
      const city = pickOne(row.cid_cities as unknown as { id: string; official_name: string } | { id: string; official_name: string }[] | null);
      const aa = pickOne(row.cid_admin_areas as unknown as { id: string; official_name: string } | { id: string; official_name: string }[] | null);
      const country = pickOne(row.cid_countries as unknown as { id: string; official_name_en: string; iso2: string | null } | { id: string; official_name_en: string; iso2: string | null }[] | null);
      const brewery = pickOne(row.cid_breweries as unknown as { id: string; official_name: string; website_url: string | null } | { id: string; official_name: string; website_url: string | null }[] | null);
      const operator = pickOne((row as Record<string, unknown>).operator as unknown as { id: string; official_name: string } | { id: string; official_name: string }[] | null);

      console.log("[getBrewerySite] relations", {
        hasCity: !!city,
        hasAdminArea: !!aa,
        hasCountry: !!country,
        hasBrewery: !!brewery,
      });

      const result: BrewerySiteDetail = {
        id: row.id as string,
        official_name: (row.official_name as string) ?? "",
        site_type: (row.site_type as string) ?? null,
        site_status: (row.site_status as string) ?? null,
        address_line_1: (row.address_line_1 as string) ?? null,
        postal_code: (row.postal_code as string) ?? null,
        latitude: row.latitude == null ? null : Number(row.latitude),
        longitude: row.longitude == null ? null : Number(row.longitude),
        opened_year: (row.opened_year as number) ?? null,
        closed_year: (row.closed_year as number) ?? null,
        taproom: (row.taproom as boolean) ?? null,
        restaurant: (row.restaurant as boolean) ?? null,
        visitor_center: (row.visitor_center as boolean) ?? null,
        tours_available: (row.tours_available as boolean) ?? null,
        company_shop: (row.company_shop as boolean) ?? null,
        city: city ? { id: city.id, name: city.official_name } : null,
        admin_area: aa ? { id: aa.id, name: aa.official_name } : null,
        country: country ? { id: country.id, name: country.official_name_en, iso2: country.iso2 } : null,
        brewery: brewery ? { id: brewery.id, name: brewery.official_name, website_url: brewery.website_url ?? null } : null,
        operator: operator ? { id: operator.id, name: operator.official_name } : null,
      };
      console.log("[getBrewerySite] success", { id: result.id, name: result.official_name });
      return result;
    } catch (e) {
      console.error("[getBrewerySite] mapping error", e, "row=", row);
      throw e;
    }
  });

